import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  Brain,
  Database,
  GitBranch,
  MessageSquare,
  Image,
  FileText,
  Zap,
  Square,
  Mic,
  Variable,
  Target,
  Link,
  Puzzle,
  Wrench,
  Code,
  Plus,
  Wifi,
} from 'lucide-react';
import composioService from '../services/composio';

const ComposioIntegrations = lazy(() => import('./ComposioIntegrations'));

// Resizable sidebar sizing
const DEFAULT_WIDTH = 320; // ~2× wider default
const MIN_WIDTH = 320;
const MAX_WIDTH = 2000;

const nodeCategories = [
  {
    name: 'Conversational UI',
    icon: MessageSquare,
    nodes: [
      { type: 'message', label: 'Message Block', icon: MessageSquare },
      { type: 'button', label: 'Button Block', icon: Square },
      { type: 'prompt', label: 'Prompt Block', icon: Mic },
      { type: 'end', label: 'End Block', icon: Square },
    ],
  },
  {
    name: 'Logic & Variables',
    icon: GitBranch,
    nodes: [
      { type: 'setVariable', label: 'Set Variable', icon: Variable },
      { type: 'condition', label: 'Condition', icon: GitBranch },
      { type: 'switch', label: 'Switch Block', icon: GitBranch },
      { type: 'intent', label: 'Intent Block', icon: Target },
    ],
  },
  {
    name: 'AI Models',
    icon: Brain,
    nodes: [
      { type: 'llm', label: 'LLM Chat', icon: MessageSquare },
      { type: 'imageGen', label: 'Image Generator', icon: Image },
      { type: 'textAnalysis', label: 'Text Analysis', icon: FileText },
    ],
  },
  {
    name: 'Integrations',
    icon: Link,
    nodes: [
      { type: 'composio', label: 'App Action', icon: Wifi },
      { type: 'apiCall', label: 'API Call', icon: Link },
      { type: 'integration', label: 'Integration', icon: Puzzle },
      { type: 'function', label: 'Function', icon: Wrench },
      { type: 'code', label: 'Code Block', icon: Code },
    ],
  },
  {
    name: 'Data Processing',
    icon: Database,
    nodes: [
      { type: 'dataInput', label: 'Data Input', icon: Database },
      { type: 'dataTransform', label: 'Transform', icon: Zap },
      { type: 'dataOutput', label: 'Data Output', icon: Database },
    ],
  },
  {
    name: 'Flow Control',
    icon: Zap,
    nodes: [
      { type: 'trigger', label: 'Trigger', icon: Zap },
      { type: 'loop', label: 'Loop', icon: GitBranch },
    ],
  },
];

const DraggableNode = ({ type, label, icon: Icon }) => {
  const [hovered, setHovered] = useState(false);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      onDragStart={(event) => onDragStart(event, type)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      draggable
      role="button"
      aria-label={`Drag ${label} node`}
      title={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        background: 'var(--panel)',
        border: `1px solid ${hovered ? 'var(--accent)' : 'color-mix(in srgb, var(--line) 20%, transparent)'}`,
        borderLeft: `2px solid ${hovered ? 'var(--accent)' : 'color-mix(in srgb, var(--line) 20%, transparent)'}`,
        color: 'var(--text)',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '11px',
        cursor: 'grab',
        borderRadius: '3px',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: hovered
          ? '0 0 6px color-mix(in srgb, var(--accent) 20%, transparent)'
          : 'none',
        userSelect: 'none',
      }}
    >
      <Icon
        size={13}
        style={{ color: 'var(--accent)', flexShrink: 0 }}
      />
      <span>{label}</span>
    </div>
  );
};

const CategorySection = ({ category, isExpanded, onToggle }) => {
  const Icon = category.icon;
  return (
    <div style={{ marginBottom: '6px' }}>
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`cat-${category.name}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          padding: '5px 6px',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--accent-2)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '10px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          borderRadius: '2px',
          transition: 'background 0.12s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            'color-mix(in srgb, var(--accent-2) 8%, transparent)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'none';
        }}
      >
        <span style={{ color: 'var(--accent-2)', fontSize: '9px' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
        <Icon size={11} style={{ color: 'var(--accent-2)', flexShrink: 0 }} />
        <span>{category.name}</span>
      </button>

      {isExpanded && (
        <div
          id={`cat-${category.name}`}
          style={{
            marginTop: '4px',
            paddingLeft: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {category.nodes.map((node) => (
            <DraggableNode
              key={node.type}
              type={node.type}
              label={node.label}
              icon={node.icon}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ width, onResize }) => {
  // Controlled/uncontrolled hybrid width
  const [selfWidth, setSelfWidth] = useState(width ?? DEFAULT_WIDTH);
  useEffect(() => {
    if (typeof width === 'number') setSelfWidth(width);
  }, [width]);
  const currentWidth = typeof width === 'number' ? width : selfWidth;

  const [showConnect, setShowConnect] = useState(false);
  const [connectedApps, setConnectedApps] = useState([]);

  useEffect(() => {
    composioService.getConnections().then(res => {
      setConnectedApps(res.connections || []);
    }).catch(() => {});
  }, [showConnect]);

  const [expandedCategories, setExpandedCategories] = useState({
    'Conversational UI': false,
    'Logic & Variables': false,
    'AI Models': false,
    'Integrations': false,
    'Data Processing': false,
    'Flow Control': false,
  });

  const toggleCategory = useCallback((categoryName) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  }, []);

  // Drag-to-resize handle (works standalone or controlled by parent)
  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = currentWidth;

      const onMouseMove = (ev) => {
        const delta = ev.clientX - startX;
        const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
        if (onResize) onResize(next);
        else setSelfWidth(next);
      };

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        document.body.style.userSelect = '';
      };

      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [currentWidth, onResize]
  );

  return (
    <div
      style={{
        width: currentWidth,
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
        flex: '0 0 auto',
        boxSizing: 'border-box',
        background: 'var(--bg-soft)',
        borderRight: '1px solid color-mix(in srgb, var(--line) 20%, transparent)',
        padding: '12px',
        overflowY: 'auto',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '12px',
        position: 'relative',
      }}
    >
      {/* Sidebar header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '12px', display: 'flex', gap: '6px' }}>
          <button
            className="af-btn"
            style={{ flex: 1, fontSize: '10px', padding: '6px 10px' }}
            onClick={() => window.dispatchEvent(new CustomEvent('ai-flow-starter'))}
            title="Create a starter flow"
          >
            Starter Flow
          </button>
          <button
            className="af-btn"
            style={{ fontSize: '10px', padding: '6px 10px' }}
            onClick={() => window.dispatchEvent(new CustomEvent('ai-flow-embed'))}
            title="Get embed code for current flow"
          >
            Embed
          </button>
        </div>

        <div
          style={{
            paddingBottom: '10px',
            borderBottom: '1px solid color-mix(in srgb, var(--line) 20%, transparent)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--accent-2)',
            }}
          >
            Node Palette
          </h2>
          <p
            className="af-text-muted"
            style={{
              margin: '4px 0 0',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '10px',
            }}
          >
            Drag nodes onto the canvas
          </p>
        </div>
      </div>

      {/* Category list */}
      <div>
        {nodeCategories.map((category) => (
          <CategorySection
            key={category.name}
            category={category}
            isExpanded={expandedCategories[category.name]}
            onToggle={() => toggleCategory(category.name)}
          />
        ))}
      </div>

      {/* Quick Tips panel */}
      <div
        className="af-panel"
        style={{
          marginTop: '20px',
          padding: '10px 12px',
        }}
      >
        <h3
          className="af-text-heading"
          style={{ marginBottom: '8px', display: 'block' }}
        >
          Quick Tips
        </h3>
        <ul
          className="af-text-muted"
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '10px',
            lineHeight: '1.8',
          }}
        >
          <li>▸ Drag nodes onto canvas</li>
          <li>▸ Connect via handles</li>
          <li>▸ Double-click to configure</li>
          <li>▸ Right-click for menu</li>
        </ul>
      </div>

      {/* Connected Apps */}
      <div
        style={{
          marginTop: '14px',
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--accent) 6%, var(--panel))',
          border: '1px solid color-mix(in srgb, var(--accent) 18%, transparent)',
          borderRadius: '4px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Wifi size={10} style={{ color: 'var(--accent)' }} />
            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-2)' }}>
              Connected Apps
            </span>
          </div>
          <button
            onClick={() => setShowConnect(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              padding: '3px 7px', fontSize: '9px', fontWeight: 600,
              background: 'color-mix(in srgb, var(--accent) 15%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)',
              color: 'var(--accent)', borderRadius: '3px', cursor: 'pointer',
              fontFamily: '"JetBrains Mono", monospace',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 25%, transparent)'}
            onMouseLeave={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 15%, transparent)'}
          >
            <Plus size={9} /> Connect
          </button>
        </div>

        {connectedApps.length === 0 ? (
          <p style={{ margin: 0, fontSize: '10px', color: 'var(--muted)', fontFamily: '"JetBrains Mono", monospace', lineHeight: 1.5 }}>
            No apps connected yet.{' '}
            <button
              onClick={() => setShowConnect(true)}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: '10px', fontFamily: 'inherit', textDecoration: 'underline' }}
            >
              Connect one →
            </button>
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {connectedApps.map(app => (
              <span
                key={app.id || app.toolkit}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 7px', borderRadius: '20px', fontSize: '9px', fontWeight: 600,
                  fontFamily: '"JetBrains Mono", monospace',
                  background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                  color: 'var(--accent-2)',
                }}
              >
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
                {app.toolkit}
              </span>
            ))}
            <button
              onClick={() => setShowConnect(true)}
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 7px', borderRadius: '20px', fontSize: '9px',
                fontFamily: '"JetBrains Mono", monospace', cursor: 'pointer',
                background: 'none', border: '1px dashed color-mix(in srgb, var(--accent) 30%, transparent)',
                color: 'var(--muted)',
              }}
            >
              + more
            </button>
          </div>
        )}
      </div>

      {/* Composio modal */}
      {showConnect && (
        <Suspense fallback={null}>
          <ComposioIntegrations onClose={() => setShowConnect(false)} />
        </Suspense>
      )}

      {/* Resize handle */}
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: '3px',
          cursor: 'col-resize',
          background: 'var(--accent)',
          opacity: 0.35,
          transition: 'opacity 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.35'; }}
      />
    </div>
  );
};

export default Sidebar;
